import { usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { useCurrentUserAsync, useLoginStateAsync } from '#src/app/shared/hooks/session';
import { isLoaded } from '#src/app/shared/utils/async';

import { useCurrentUserAddressIsComplete } from '../useCurrentUserAddressIsComplete';

type Result = ModelHookResult<{
	enabled: boolean;
}>;

/**
 * Whether the authenticated single-address flow renders the Google-autocomplete address field, for
 * callers that render during SSR with user data disabled — where `useLoginState` / `useCurrentUser`
 * throw and only their `…Async` counterparts are safe.
 *
 * Resolves `false` until **both** the login state and the current user have loaded. Keying only on
 * the login state would report an authenticated visitor as eligible while their user record is still
 * in flight, since an absent user reads as an incomplete address.
 *
 * `useSingleAddressFieldForAuthenticated` is the sync equivalent, for callers inside the sign form.
 */
export function useSingleAddressFieldForAuthenticatedAsync(): Result {
	const {
		data: { fullAddressCollectionEnabled },
	} = usePetitionDetailsPageUserData();

	const loginStateAsync = useLoginStateAsync();
	const currentUserAsync = useCurrentUserAsync();

	const loginStateLoaded = isLoaded(loginStateAsync);
	const currentUserLoaded = isLoaded(currentUserAsync);

	const {
		data: { isComplete: currentUserAddressIsComplete },
	} = useCurrentUserAddressIsComplete(currentUserLoaded ? currentUserAsync.value : undefined);

	return {
		data: {
			enabled:
				loginStateLoaded &&
				currentUserLoaded &&
				loginStateAsync.value === 'AUTHENTICATED' &&
				!currentUserAddressIsComplete &&
				fullAddressCollectionEnabled,
		},
	};
}
